import { useRef, useState, type CSSProperties } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomInsideComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor, rgbaColor } from '@/helpers/utils';
import PropTypes from 'prop-types';
import { Button, Card, Form } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import numberHumanizer from '@/helpers/numberHumanizer';
import PlotSkeleton from './PlotSkeleton';

echarts.use([
  LineChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomInsideComponent
]);

const HELP = {
  backlink:
    'تجمیعی: موجودی بک‌لینک در پایان هر ماه. لینکی که در یک ماه حذف شده، از همان ماه به بعد از جمع کم می‌شود.\nجدید: بک‌لینک‌هایی که برای اولین بار در همان ماه دیده شده‌اند.',
  domain:
    'تجمیعی: دامنه‌هایی که در پایان ماه هنوز حداقل یک بک‌لینک زنده دارند. اگر همه لینک‌های یک دامنه حذف شود، از ماه مشاهدهٔ حذف کم می‌شود.\nجدید: دامنه‌هایی که اولین بک‌لینک آن‌ها در همان ماه دیده شده است.'
};

type DiscoveryTrendData = {
  categories?: string[];
  values?: number[];
  new?: number[];
  lost?: number[];
  cumulative?: number[];
};

const seriesValues = (
  data: DiscoveryTrendData | null | undefined,
  mode: string
): number[] => {
  if (mode === 'fresh') return data?.new ?? data?.values ?? [];
  return data?.cumulative ?? data?.values ?? [];
};

/** API buckets are calendar months; `points` = how many latest months to show. */
const RANGE_OPTIONS = [
  { key: 'month', label: '۱ ماه', points: 1 },
  { key: 'quarter', label: '۳ ماه', points: 3 },
  { key: 'halfYear', label: '۶ ماه', points: 6 },
  { key: 'year', label: '۱ سال', points: 12 },
  { key: 'all', label: 'همه', points: Infinity }
];

const TREND_MODES = [
  { key: 'cumulative', label: 'تجمیعی' },
  { key: 'fresh', label: 'جدید' }
] as const;

const sliceTrendData = (
  data: DiscoveryTrendData | null | undefined,
  rangeKey: string
) => {
  if (!data) return data;
  const option = RANGE_OPTIONS.find(o => o.key === rangeKey);
  const points = option?.points ?? Infinity;
  const categories = data.categories ?? [];
  const len = categories.length;
  if (!len || points === Infinity || points >= len) return data;

  const start = len - points;
  const slice = <T,>(arr: T[] | undefined): T[] | undefined =>
    Array.isArray(arr) ? arr.slice(start) : arr;

  return {
    ...data,
    categories: slice(categories),
    new: slice(data.new),
    lost: slice(data.lost),
    cumulative: slice(data.cumulative),
    values: slice(data.values ?? data.cumulative)
  };
};

const formatMonthCategory = (value: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(value));
  if (!match) return value;
  return `${match[2]}/${match[1].slice(2)}`;
};

const formatMonthTooltip = (value: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(value));
  if (!match) return value;
  return `${match[1]}/${match[2]}`;
};

/** Aim for ~maxLabels tick marks on the visible window. */
const axisLabelInterval = (
  categoryCount: number,
  maxLabels: number = 16
): number => {
  if (categoryCount <= maxLabels) return 0;
  return Math.ceil(categoryCount / maxLabels) - 1;
};

const getOption = (
  data: DiscoveryTrendData | null | undefined,
  mode: string
) => {
  const categories = data?.categories ?? [];
  const fresh = data?.new ?? [];
  const lost = data?.lost ?? [];
  const cumulative = data?.cumulative ?? data?.values ?? [];
  const points = seriesValues(data, mode);
  const seriesColor = getColor('navbar-vertical-default-link-active-color');
  const labelMax = typeof window !== 'undefined' && window.innerWidth < 768 ? 10 : 16;
  const labelInterval = axisLabelInterval(categories.length, labelMax);
  const rotateLabels = categories.length > 10 ? 40 : 0;

  return {
    textStyle: {
      fontFamily: 'dana'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line'
      },
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      borderWidth: 1,
      transitionDuration: 0,
      textStyle: {
        fontWeight: 500,
        fontSize: 12,
        color: getColor('gray-1100')
      },
      formatter: (params: { dataIndex?: number; name?: string }[]) => {
        const index = params[0]?.dataIndex ?? 0;
        const name = formatMonthTooltip(params[0]?.name ?? '');
        const rows = [
          `<div class="fw-bolder mb-1">${name}</div>`,
          `<div>موجود: ${numberHumanizer(cumulative[index] ?? 0)}</div>`
        ];
        if (fresh.length) {
          rows.push(`<div>جدید: ${numberHumanizer(fresh[index] ?? 0)}</div>`);
        }
        if (lost.length) {
          rows.push(
            `<div>حذف‌شده: ${numberHumanizer(lost[index] ?? 0)}</div>`
          );
        }
        return rows.join('');
      }
    },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: 0,
        end: 100
      }
    ],
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      splitLine: {
        show: false,
        lineStyle: {
          color: rgbaColor(getColor('primary'), 0.1)
        },
        interval: 0
      },
      axisLine: {
        lineStyle: {
          color: rgbaColor(getColor('primary'), 0.1)
        }
      },
      axisTick: {
        show: false,
        length: 10,
        lineStyle: {
          color: rgbaColor(getColor('primary'), 0.1)
        }
      },
      axisPointer: {
        lineStyle: {
          color: getColor('gray-300')
        }
      },
      axisLabel: {
        color: getColor('gray-400'),
        fontWeight: 600,
        fontSize: 11,
        rotate: rotateLabels,
        margin: rotateLabels ? 12 : 8,
        hideOverlap: true,
        interval: labelInterval,
        formatter: formatMonthCategory
      }
    },
    yAxis: {
      type: 'value',
      splitLine: {
        show: true,
        lineStyle: {
          color: rgbaColor(getColor('primary'), 0.1)
        },
        interval: 0
      },
      axisLabel: {
        show: true
      },
      axisTick: {
        show: true,
        length: 10,
        lineStyle: {
          color: rgbaColor(getColor('primary'), 0.1)
        }
      },
      axisLine: { show: true }
    },
    series: [
      {
        type: 'line',
        smooth: false,
        data: points,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: categories.length <= 3 ? 8 : 5,
        itemStyle: {
          color: seriesColor,
          borderColor: seriesColor,
          borderWidth: 2
        },
        lineStyle: {
          color: seriesColor,
          width: 2
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: rgbaColor(seriesColor, 0.28) },
              { offset: 1, color: rgbaColor(seriesColor, 0.02) }
            ]
          }
        },
        emphasis: {
          scale: true,
          lineStyle: {
            color: seriesColor,
            width: 2
          }
        }
      }
    ],
    grid: {
      right: '3%',
      left: '10%',
      bottom: rotateLabels ? '16%' : '10%',
      top: '5%'
    }
  };
};

const CHART_CARDS = [
  { subject: 'backlink', title: 'روند کشف بک‌لینک' },
  { subject: 'domain', title: 'روند کشف دامنه‌ها' }
] as const;

type TrendSubject = (typeof CHART_CARDS)[number]['subject'];

const DiscoveryTrendCard = ({
  title,
  help,
  data,
  chartStyle,
  loading = false
}: {
  title: string;
  help: string;
  data: DiscoveryTrendData | null | undefined;
  chartStyle: CSSProperties;
  loading?: boolean;
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const [mode, setMode] = useState('cumulative');
  const [range, setRange] = useState('all');
  const viewData = sliceTrendData(data, range);

  return (
    <Card ref={cardRef}>
      <FalconCardHeader
        title={
          <>
            {title}
            {help ? <HelpTooltip html={help} /> : null}
          </>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Select
              size="sm"
              value={range}
              onChange={event => setRange(event.target.value)}
              aria-label={`بازه ${title}`}
              className="w-auto"
            >
              {RANGE_OPTIONS.map(option => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <div
              className="d-inline-flex align-items-center bg-100 border rounded-3 p-1 gap-1"
              role="group"
              aria-label={`نوع ${title}`}
            >
              {TREND_MODES.map(option => {
                const active = mode === option.key;
                return (
                  <Button
                    key={option.key}
                    type="button"
                    size="sm"
                    variant={active ? 'falcon-default' : 'link'}
                    className={`d-inline-flex align-items-center gap-1 px-2 py-1 fs--2 fw-semi-bold text-nowrap rounded-2 ${
                      active
                        ? 'text-primary shadow-sm'
                        : 'text-700 text-decoration-none shadow-none'
                    }`}
                    aria-pressed={active}
                    onClick={() => setMode(option.key)}
                  >
                    <span
                      className={`small-dot m-0 flex-shrink-0 ${
                        active ? 'bg-primary' : 'bg-500'
                      }`}
                      aria-hidden="true"
                    />
                    {option.label}
                  </Button>
                );
              })}
            </div>
            <CardChartDropDown cardRef={cardRef} chartRef={chartRef} />
          </div>
        }
      />
      <Card.Body>
        {loading ? (
          <PlotSkeleton height={chartStyle.height ?? 320} />
        ) : (
          <ReactEChartsCore
            echarts={echarts}
            ref={chartRef}
            option={getOption(viewData, mode)}
            style={chartStyle}
            notMerge
          />
        )}
      </Card.Body>
    </Card>
  );
};

const DiscoveryTrendCharts = ({
  backlinkData,
  domainData,
  chartStyle = { height: '320px' },
  loading = false
}: Record<string, any>) => {
  const dataBySubject: Record<TrendSubject, DiscoveryTrendData | null | undefined> =
    {
      backlink: backlinkData,
      domain: domainData
    };

  return (
    <div className="d-flex flex-column gap-3 w-100">
      {CHART_CARDS.map(({ subject, title }) => (
        <DiscoveryTrendCard
          key={subject}
          title={title}
          help={HELP[subject]}
          data={dataBySubject[subject]}
          chartStyle={chartStyle}
          loading={loading}
        />
      ))}
    </div>
  );
};

DiscoveryTrendCharts.propTypes = {
  backlinkData: PropTypes.shape({
    categories: PropTypes.array,
    values: PropTypes.array,
    new: PropTypes.array,
    lost: PropTypes.array,
    cumulative: PropTypes.array
  }),
  domainData: PropTypes.shape({
    categories: PropTypes.array,
    values: PropTypes.array,
    new: PropTypes.array,
    lost: PropTypes.array,
    cumulative: PropTypes.array
  }),
  chartStyle: PropTypes.object,
  loading: PropTypes.bool
};

export default DiscoveryTrendCharts;
